import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  IconPowerOn,
  IconPowerOff,
  IconRestart,
  IconLogout,
  IconDelete,
  IconInternet,
  IconMigrate,
} from "./icon";
import { useConfirmStore } from "modal/confirm/store";
import { useTaskStore } from "store/task";
import { ActionGroup, Button } from "./styles";
import { instanceAPI } from "api";
import MigrateModal from "./migrateModal";
import { getTaskData } from "api/taskStatus";

const confirmCopy = {
  on: {
    title: "VDI 전원 켜기",
    desc: "확인 시 선택한 VDI들의 전원이 켜집니다.",
  },
  off: {
    title: "VDI 전원 끄기",
    desc: "확인 시 선택한 VDI들의 전원이 꺼집니다.",
  },
  restart: {
    title: "VDI 재시작",
    desc: "확인 시 선택한 VDI들이 재시작됩니다.",
  },
};

const InstanceAction = ({ selectInstances }) => {
  const { addTask } = useTaskStore();
  const { openConfirm } = useConfirmStore();

  const [isMigrate, setIsMigrate] = useState(false);

  const selectedCount = selectInstances?.length ?? 0;
  const singleSelected = selectedCount === 1;
  const selectInstance = singleSelected ? selectInstances[0] : null;
  const [isInternet, setIsInternet] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const data = (
        await getTaskData(() => instanceAPI.getInstance(selectInstance.name))
      ).result.data;

      setIsInternet(data.internet);
    };

    if (selectInstance) fetchData();
  }, [selectInstance]);

  const onPowerInstance = useCallback(
    (action) => {
      const { title, desc } = confirmCopy[action];
      openConfirm({
        title,
        desc,
        onConfirm: async () => {
          for (const instance of selectInstances) {
            const res = await instanceAPI.power(instance.name, action);
            addTask({
              name: instance.name,
              id: res.data.task_id,
              job: `VDI Power : ${action}`,
              state: "pending",
            });
          }
        },
      });
    },
    [addTask, openConfirm, selectInstances]
  );

  const onLogout = useCallback(() => {
    openConfirm({
      title: "VDI 로그아웃",
      desc: "확인 시 선택한 VDI들이 로그아웃 됩니다.",
      onConfirm: async () => {
        for (const instance of selectInstances) {
          await instanceAPI.logoff(instance.name);
        }
      },
    });
  }, [openConfirm, selectInstances]);

  const onDelete = useCallback(() => {
    openConfirm({
      title: "VDI 삭제",
      desc: "복구할 수 없습니다. 정말 삭제하시겠습니까?",
      confirmMessage: "삭제하겠습니다",
      onConfirm: async () => {
        for (const instance of selectInstances) {
          const res = await instanceAPI.deleteInstance(instance.name);
          addTask({
            name: instance.name,
            id: res.data.task_id,
            job: `VDI Power : delete`,
            state: "pending",
          });
        }
      },
    });
  }, [addTask, openConfirm, selectInstances]);

  const onInternet = useCallback(async () => {
    const res = await getTaskData(() =>
      instanceAPI.getInstance(selectInstances[0].name)
    );

    if (res?.result.data.internet) {
      openConfirm({
        title: "인터넷 끄기",
        desc: `VDI ${selectInstances[0].name} 인터넷을 끕니다.`,
        onConfirm: async () => {
          for (const instance of selectInstances) {
            await instanceAPI.internetOff(instance.name);
          }
        },
      });
    } else {
      openConfirm({
        title: "인터넷 켜기",
        desc: `VDI ${selectInstances[0].name} 인터넷을 켭니다.`,
        onConfirm: async () => {
          for (const instance of selectInstances) {
            await instanceAPI.internetOn(instance.name);
          }
        },
      });
    }
  }, [openConfirm, selectInstances]);

  const onMigrate = useCallback(() => {
    setIsMigrate(true);
  }, []);

  const actions = useMemo(
    () => [
      {
        key: "migrate",
        label: "Migrate",
        variant: "info",
        icon: <IconMigrate />,
        onClick: onMigrate,
        disabled: !singleSelected,
      },
      {
        key: "internet",
        label: isInternet ? "Internet On" : "Internet Off",
        variant: "neutral",
        icon: <IconInternet />,
        onClick: onInternet,
        disabled: !singleSelected,
      },
      {
        key: "on",
        label: "Power On",
        variant: "success",
        icon: <IconPowerOn />,
        onClick: () => onPowerInstance("on"),
      },
      {
        key: "off",
        label: "Power Off",
        variant: "warning",
        icon: <IconPowerOff />,
        onClick: () => onPowerInstance("off"),
      },
      {
        key: "restart",
        label: "Restart",
        variant: "info",
        icon: <IconRestart />,
        onClick: () => onPowerInstance("restart"),
      },
      {
        key: "logout",
        label: "Logout",
        variant: "neutral",
        icon: <IconLogout />,
        onClick: onLogout,
      },
      {
        key: "delete",
        label: "Delete",
        variant: "danger",
        icon: <IconDelete />,
        onClick: onDelete,
      },
    ],
    [
      onMigrate,
      onInternet,
      onPowerInstance,
      onLogout,
      onDelete,
      singleSelected,
      isInternet,
    ]
  );

  return (
    <ActionGroup className="btn-row">
      {actions.map((a) => (
        <Button
          key={a.key}
          $variant={a.variant}
          disabled={!selectInstances?.length || a.disabled}
          onClick={a.onClick}
        >
          <i>{a.icon}</i>
          {a.label}
        </Button>
      ))}

      {isMigrate && (
        <MigrateModal
          instanceName={selectInstances[0].name}
          onClose={() => {
            setIsMigrate(false);
          }}
        />
      )}
    </ActionGroup>
  );
};

export default InstanceAction;
